import { useState } from "react";
import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const fetcher = (url: string) => fetch(url, { credentials: "include" }).then((res) => res.json());

const REGIONS = [
  { id: "global", label: "Global" },
  { id: "us", label: "United States" },
  { id: "eu", label: "Europe" },
];

export function GeoFanGrowthPanel() {
  const [region, setRegion] = useState("global");
  const { data, isLoading } = useSWR(`/marketing/growth/geofans?region=${region}`, fetcher);
  const topCities = data?.top_cities ?? [];
  const overlap = data?.competitor_overlap ?? [];
  const genres = data?.genre_clusters ?? [];
  const postingTimes = data?.posting_times ?? {};
  const boosts = data?.recommended_boosts ?? [];

  return (
    <Card className="space-y-4 border-border/70 bg-background/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Geo-Fan Growth</p>
          <p className="text-xs text-muted-foreground">Where to lean in next.</p>
        </div>
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-40 bg-background/50 text-foreground">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent className="bg-background/40 text-foreground">
            {REGIONS.map((entry) => (
              <SelectItem key={entry.id} value={entry.id}>
                {entry.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading geo insights…</p>
      ) : (
        <div className="space-y-4">
          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Top potential cities</p>
            <div className="mt-2 space-y-2">
              {topCities.map((city: { city: string; potential: number }) => (
                <div key={city.city} className="flex items-center justify-between rounded-xl border border-border/70 bg-background/40 p-2 text-sm text-foreground/80">
                  <span>{city.city}</span>
                  <Badge variant="outline" className="border-success/30 text-success">
                    {city.potential}%
                  </Badge>
                </div>
              ))}
              {!topCities.length ? <p className="text-xs text-muted-foreground">No city data.</p> : null}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Competitor overlap</p>
            <div className="mt-2 space-y-2">
              {overlap.map((entry: { artist: string; overlap: number }) => (
                <div key={entry.artist} className="flex items-center justify-between rounded-xl border border-border/70 bg-background/40 p-2 text-sm text-foreground/80">
                  <span>{entry.artist}</span>
                  <span className="text-xs text-muted-foreground">{Math.round(entry.overlap * 100)}% shared</span>
                </div>
              ))}
              {!overlap.length ? <p className="text-xs text-muted-foreground">No competitor data.</p> : null}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Genre clusters</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {genres.map((genre: string) => (
                <Badge key={genre} className="bg-muted/60 text-foreground">
                  {genre}
                </Badge>
              ))}
              {!genres.length ? <p className="text-xs text-muted-foreground">No clusters identified.</p> : null}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Posting windows</p>
            <div className="mt-2 space-y-2">
              {Object.entries(postingTimes).map(([city, window]) => (
                <div key={city} className="rounded-xl border border-border/70 bg-background/40 p-2 text-sm text-foreground/80">
                  <span className="font-semibold">{city}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{String(window)}</span>
                </div>
              ))}
              {!Object.keys(postingTimes).length ? <p className="text-xs text-muted-foreground">No timing data.</p> : null}
            </div>
          </section>

          <section>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Paid boosts</p>
            <div className="mt-2 space-y-2">
              {boosts.map((boost: { city: string; budget: number }) => (
                <div key={boost.city} className="rounded-xl border border-border/70 bg-background/40 p-2 text-sm text-foreground/80">
                  {boost.city}: ${boost.budget}
                </div>
              ))}
              {!boosts.length ? <p className="text-xs text-muted-foreground">No boost recommendations.</p> : null}
            </div>
          </section>
        </div>
      )}
    </Card>
  );
}
